// Global Instructions Rule Applied!
//
// The header the three Commitment forms share: the record name with the
// action buttons on the same row, and under it the record links to what the
// commitment belongs to (investor, account, fund).
//
// Buttons follow the fixed full-form grammar: [Delete] [Cancel | Close]
// [Create → Save]; Save and Cancel only while there are unsaved changes.

import React from 'react'
import { Button, Tooltip, Typography } from 'antd'

const { Title } = Typography

export interface CommitmentHeaderLink {
  label: string
  text: string | null
  onClick: () => void
}

interface Props {
  title: string
  links?: CommitmentHeaderLink[]
  creating?: boolean
  dirty: boolean
  saving: boolean
  onSave: () => void
  onCancel: () => void
  onClose: () => void
  /** Absent = the record cannot be deleted here (a new record, a pocket). */
  onDelete?: () => void
  deleting?: boolean
  deleteDisabledReason?: string | null
}

export function CommitmentFormHeader({
  title,
  links = [],
  creating = false,
  dirty,
  saving,
  onSave,
  onCancel,
  onClose,
  onDelete,
  deleting = false,
  deleteDisabledReason = null
}: Props): React.ReactElement {
  const shownLinks = links.filter((link) => link.text)
  return (
    <div className='mb-3'>
      <div className='flex items-center justify-between'>
        <Title level={4} className='!mb-0'>
          {title}
        </Title>
        <div className='flex items-center gap-2'>
          {onDelete ? (
            <Tooltip title={deleteDisabledReason ?? undefined}>
              <span>
                <Button danger disabled={saving || deleteDisabledReason != null} loading={deleting} onClick={onDelete}>
                  Delete
                </Button>
              </span>
            </Tooltip>
          ) : null}
          {dirty ? (
            <Button disabled={saving} onClick={onCancel}>
              Cancel
            </Button>
          ) : creating ? null : (
            <Button onClick={onClose}>Close</Button>
          )}
          {creating ? (
            <Button type='primary' loading={saving} onClick={onSave}>
              Create
            </Button>
          ) : dirty ? (
            <Button type='primary' loading={saving} onClick={onSave}>
              Save
            </Button>
          ) : null}
        </div>
      </div>
      {shownLinks.length > 0 ? (
        <div className='mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm'>
          {shownLinks.map((link) => (
            <span key={link.label}>
              <span className='text-gray-500'>{link.label}: </span>
              <button
                type='button'
                className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
                onClick={link.onClick}
              >
                {link.text}
              </button>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  )
}

export default CommitmentFormHeader
